import React, { useEffect, useRef, useState, useCallback } from 'react';

const MultiAnnouncementBar = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const lastTimestampRef = useRef(0);
  const speed = 0.8; // Keep your original speed

  // Fetch messages from API
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await fetch('https://evercorenutrition.com/ev_admin/api/messages/');
        const data = await response.json();
        
        if (data.success && data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        } else {
          setMessages([]);
        }
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        setError('Unable to load announcements');
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  // Duplicate messages for seamless loop (only if we have messages)
  const duplicatedMessages = messages.length > 0 ? [...messages, ...messages, ...messages] : []; // Tripled for smoother loop

  // Auto-scroll animation - FIXED to never stop
  useEffect(() => {
    if (loading || messages.length === 0) return;

    let animationId;
    let lastTimestamp = 0;
    let accumulatedDelta = 0;
    
    const animate = (timestamp) => {
      if (!scrollRef.current) return;
      
      // Initialize lastTimestamp
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
        animationId = requestAnimationFrame(animate);
        return;
      }
      
      // Calculate delta time (limit to 16ms for smooth animation)
      const delta = Math.min(16, timestamp - lastTimestamp);
      lastTimestamp = timestamp;
      
      if (!isPaused) {
        // Accumulate delta for smoother movement
        accumulatedDelta += delta;
        
        // Move every frame for continuous scrolling
        if (accumulatedDelta >= 1) {
          const moveAmount = speed * (accumulatedDelta / 16);
          scrollPositionRef.current += moveAmount;
          accumulatedDelta = 0;
          
          // Reset when half of the duplicated content has scrolled
          if (scrollRef.current) {
            const containerWidth = scrollRef.current.scrollWidth / 3;
            if (scrollPositionRef.current >= containerWidth) {
              scrollPositionRef.current = 0;
            }
            
            // Apply scroll position
            scrollRef.current.scrollLeft = scrollPositionRef.current;
          }
        }
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isPaused, loading, messages.length]);

  // Reset scroll position when messages change
  useEffect(() => {
    if (!loading && messages.length > 0 && scrollRef.current) {
      scrollPositionRef.current = 0;
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = 0;
      }
    }
  }, [messages, loading]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Show loading state
  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.container}>
          <div style={styles.loadingText}>
            <span>Loading announcements...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error or empty state
  if (error || messages.length === 0) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.container}>
          <div style={styles.emptyText}>
            <span>{error || 'No announcements available'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        {/* Left gradient fade */}
        <div style={styles.leftGradient}></div>
        
        {/* Scrolling announcements */}
        <div 
          ref={scrollRef}
          style={styles.scrollContainer}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div style={styles.announcementTrack}>
            {duplicatedMessages.map((message, index) => (
              <div 
                key={`${message.id}-${index}`}
                style={styles.announcementItem}
                className="announcement-item"
              >
                <div style={styles.textContainer}>
                  <span style={styles.title}>{message.title}</span>
                  <span style={styles.separator}>:</span>
                  <span style={styles.content}>{message.content}</span>
                </div>
                
                <span style={styles.divider}>•</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Right gradient fade */}
        <div style={styles.rightGradient}></div>
        
        {/* Pause indicator */}
        
      </div>
    </div>
  );
};

// YOUR ORIGINAL CSS - UNCHANGED
const styles = {
wrapper: {
    backgroundColor: '#1a1a2e',
    borderBottom: '2px solid #b7a9dd',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    maxWidth: '100vw',
    overflow: 'hidden',
  },
container: {
    position: 'relative',
    maxWidth: '100vw',
    overflow: 'hidden',
    backgroundColor: '#1a1a2e',
    width: '100%',
  },
scrollContainer: {
    overflowX: 'hidden',
    overflowY: 'hidden',
    whiteSpace: 'nowrap',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    cursor: 'pointer',
    padding: '10px 0',
    WebkitOverflowScrolling: 'touch',
  },
  announcementTrack: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0px',
    whiteSpace: 'nowrap'
  },
  announcementItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 24px',
    textDecoration: 'none',
    color: '#fff',
    transition: 'all 0.2s ease',
    position: 'relative'
  },
  textContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  title: {
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    background:  '#4357c9',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent'
  },
  separator: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#28118d'
  },
  content: {
    fontSize: '13px',
    fontWeight: '500',
    letterSpacing: '0.3px',
    color: '#e0e0e0'
  },
  dateBadge: {
    fontSize: '11px',
    fontWeight: '400',
    color: '#888',
    backgroundColor: 'rgba(255,255,255,0.08)',
    padding: '3px 8px',
    borderRadius: '20px',
    letterSpacing: '0.3px'
  },
  divider: {
    marginLeft: '4px',
    color: 'rgb(221, 183, 15)',
    fontSize: '16px',
    fontWeight: 'normal'
  },
  leftGradient: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '60px',
    background: 'linear-gradient(90deg, #1a1a2e 0%, rgba(26,26,46,0) 100%)',
    pointerEvents: 'none',
    zIndex: 2
  },
  rightGradient: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '60px',
    background: 'linear-gradient(270deg, #1a1a2e 0%, rgba(26,26,46,0) 100%)',
    pointerEvents: 'none',
    zIndex: 2
  },
  pauseIndicator: {
    position: 'absolute',
    top: '50%',
    right: '20px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: '20px',
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: '500',
    color: '#fff',
    zIndex: 3,
    pointerEvents: 'none',
    backdropFilter: 'blur(4px)'
  },
  loadingText: {
    padding: '10px 20px',
    textAlign: 'center',
    color: '#aaa',
    fontSize: '13px'
  },
  emptyText: {
    padding: '10px 20px',
    textAlign: 'center',
    color: '#5470bd',
    fontSize: '13px'
  }
};

// Add hover styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  .announcement-item:hover {
    opacity: 0.85;
    transform: translateY(-1px);
    cursor: pointer;
  }
  .announcement-item:hover .announcement-title {
    text-decoration: underline;
  }
`;
document.head.appendChild(styleSheet);

export default MultiAnnouncementBar;